import type { JSONContent } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { ChangeEvent, FC } from 'react';
import { useIntl } from 'react-intl';

import {
  getExtensions,
  isToolDisabled,
  TOOLS,
  EditorStyled,
} from '@xing-com/crate-articles-article-editor';

import * as Styled from './detailed-description-editor.styles';

const CustomTools = [
  TOOLS.BoldTool,
  TOOLS.ItalicTool,
  TOOLS.LinkTool,
  TOOLS.HeadingH1Tool,
  TOOLS.HeadingH2Tool,
  TOOLS.HeadingH3Tool,
  TOOLS.NumberedListTool,
  TOOLS.BulletListTool,
];

export type DescriptionDetailedContent = JSONContent;

type Props = {
  defaultContent: JSONContent;
  headline: string;
  onContentChange?: (content: JSONContent) => void;
  onHeadlineChange?: (headline: string) => void;
};
export const DetailedDescriptionEditor: FC<Props> = ({
  defaultContent,
  headline,
  onContentChange = () => undefined,
  onHeadlineChange = () => undefined,
}) => {
  const intl = useIntl();

  const editor = useEditor({
    content: defaultContent,
    extensions: getExtensions({ intl }),
    editable: true,
    onUpdate: ({ editor }) => {
      onContentChange(editor.getJSON());
    },
  });

  const enabledTools = CustomTools.filter((tool) => tool.enabled);

  return (
    <Styled.Container>
      {editor ? (
        <Styled.ToolBarContainer>
          {enabledTools.map((tool, index) => {
            const Tool = tool.component;
            const notAllowedNodes = tool.notAllowedNodes;
            const disabled = isToolDisabled(editor, notAllowedNodes);

            // @ts-expect-error we are not using video or image
            // TODO: fix this type
            return <Tool key={index} editor={editor} disabled={disabled} />;
          })}
        </Styled.ToolBarContainer>
      ) : null}
      <EditorStyled.HeadlineInput
        value={headline}
        placeholder={intl.formatMessage({ id: 'EP_EDITOR_TITLE_PLACEHOLDER' })}
        multiline
        maxLength={120}
        onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
          onHeadlineChange(ev.target.value);
        }}
      />
      <EditorContent editor={editor} />
    </Styled.Container>
  );
};
